/**
 * The four P3 question types, read the same way everywhere on the client:
 * what a scale offers, what a matrix's rows and columns are, when such an
 * answer counts as blank, and how each reads as one line of text. Mirrors
 * server/src/lib/questionTypes.ts — keep in lockstep.
 */
import type { FieldMatrixMeta, FieldOption, FieldScaleMeta, FieldUiMeta } from '../shared/types';

export const SCALE_MAX_TOP = 10;

/** The numbers a 'scale' field offers: 1..5 unless its meta says otherwise. */
export function scaleRange(meta: FieldUiMeta | undefined): FieldScaleMeta {
  const s = meta?.scale;
  const min: 0 | 1 = s?.min === 0 ? 0 : 1;
  const top = Number(s?.max);
  const max = Number.isFinite(top) ? Math.max(min + 1, Math.min(SCALE_MAX_TOP, Math.round(top))) : 5;
  return {
    min, max,
    ...(s?.minLabel?.trim() ? { minLabel: s.minLabel.trim() } : {}),
    ...(s?.maxLabel?.trim() ? { maxLabel: s.maxLabel.trim() } : {}),
  };
}

/** A scale answer as its number, or null when there is none (0 is an answer). */
export const scaleValue = (v: unknown): number | null => {
  if (v === undefined || v === null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).trim());
  return Number.isFinite(n) ? n : null;
};

const cleanOptions = (list: unknown): FieldOption[] =>
  (Array.isArray(list) ? list : [])
    .map((o) => ({
      value: String((o as FieldOption)?.value ?? '').trim(),
      text: String((o as FieldOption)?.text ?? (o as FieldOption)?.value ?? '').trim(),
    }))
    .filter((o, i, a) => o.value && a.findIndex((x) => x.value === o.value) === i);

/** A 'matrix' field's rows and columns, each list deduplicated by value. */
export function matrixMeta(meta: FieldUiMeta | undefined): FieldMatrixMeta {
  const m = meta?.matrix;
  return { rows: cleanOptions(m?.rows), columns: cleanOptions(m?.columns), multi: !!m?.multi };
}

/** A matrix answer as the object it should be, or null for any other shape. */
export function matrixAnswer(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

const cellBlank = (x: unknown) => x === undefined || x === null || x === '' || (Array.isArray(x) && x.length === 0);

/** A matrix counts as blank when no row holds an answer. */
export function matrixBlank(v: unknown): boolean {
  const o = matrixAnswer(v);
  if (!o) return cellBlank(v);
  return Object.values(o).every(cellBlank);
}

/** The rows a matrix answer leaves unanswered. */
export function matrixMissingRows(meta: FieldMatrixMeta, v: unknown): FieldOption[] {
  const o = matrixAnswer(v) || {};
  return meta.rows.filter((r) => cellBlank(o[r.value]));
}

/** A matrix as text: "Speed: Good; Price: Poor". Labels when the field is known, keys otherwise. */
export function matrixText(v: unknown, meta?: FieldMatrixMeta): string {
  const o = matrixAnswer(v);
  if (!o) return '';
  const rowLabel = (k: string) => meta?.rows.find((r) => r.value === k)?.text || k;
  const colLabel = (c: unknown) => (meta?.columns.find((x) => x.value === String(c))?.text || String(c));
  // The form's row order when the field is known; the answer's own otherwise.
  const known = meta?.rows.map((r) => r.value) || [];
  const keys = [...known.filter((k) => k in o), ...Object.keys(o).filter((k) => !known.includes(k))];
  return keys.map((k) => [k, o[k]] as const)
    .filter(([, x]) => !cellBlank(x))
    .map(([k, x]) => `${rowLabel(k)}: ${(Array.isArray(x) ? x : [x]).map(colLabel).join(', ')}`)
    .join('; ');
}

/** A ranking as text: "1. Price, 2. Speed". */
export function rankingText(v: unknown, options?: FieldOption[]): string {
  if (!Array.isArray(v)) return '';
  const label = (x: unknown) => options?.find((o) => o.value === String(x))?.text || String(x);
  return v.map((x, i) => `${i + 1}. ${label(x)}`).join(', ');
}

/** 2026-09-10T14:30, with optional seconds and an optional offset or Z. */
export const DATETIME_RE = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(:\d{2})?(Z|[+-]\d{2}:\d{2})?$/;

/** The date part of a datetime answer, for the date window and the summary. */
export const datetimeDate = (s: string): string => s.slice(0, 10);

/** What a datetime-local box shows for a stored answer: the local wall-clock part, offset dropped. */
export const datetimeLocalPart = (v: unknown): string => {
  const s = typeof v === 'string' ? v : '';
  return DATETIME_RE.test(s) ? s.slice(0, 16) : '';
};

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * A datetime-local value with this browser's offset for THAT moment appended
 * (2026-09-10T14:30 → 2026-09-10T14:30-05:00), so the stored answer says
 * when it was in the respondent's own time and where that sits in the world.
 */
export function withLocalOffset(local: string): string {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(local)) return local;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return local;
  const off = -d.getTimezoneOffset();
  const abs = Math.abs(off);
  return `${local.slice(0, 16)}${off >= 0 ? '+' : '-'}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}
